import React, { useState, useEffect } from 'react';
import { Menu, X, ChevronRight, ArrowRight } from 'lucide-react';

const LOGO_SRC = 'https://assets.auxodev.dk/cdn-cgi/image/width=240,quality=90,format=auto/assets/c8fc1da7-d576-4041-8666-592c544bae24/destilleriet-als-logo-mini_b0fc93ac.png';

const NAV_ITEMS = [
  { id: 'intro', label: 'Introduktion', num: '01' },
  { id: 'story', label: 'Brandfilm', num: '02' },
  { id: 'experience', label: 'The Experience', num: '03' },
  { id: 'recognition', label: 'Awards', num: '04' },
];

export default function Navigation({ activeSection = 'hero' }) {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Monitor scroll distance for dynamic header appearance
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 80);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();

    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Lock body scroll when mobile menu is open
  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [mobileMenuOpen]);

  // Handle escape key to close mobile menu
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && mobileMenuOpen) {
        setMobileMenuOpen(false);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [mobileMenuOpen]);

  const scrollToAnchor = (id) => {
    setMobileMenuOpen(false);

    if (id === 'hero' || id === 'top') {
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    const el = document.getElementById(id);
    if (el) {
      const isFullscreenSticky = id === 'intro' || id === 'story';
      const navOffset = isFullscreenSticky ? 0 : 60;
      const elementPosition = el.getBoundingClientRect().top + window.scrollY;
      const offsetPosition = Math.max(0, elementPosition - navOffset);

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth',
      });
    }
  };

  return (
    <>
      {/* Fixed Header Bar */}
      <header
        role="banner"
        className={`fixed top-0 left-0 right-0 z-40 transition-all duration-500 ${
          isScrolled
            ? 'opacity-100 translate-y-0 pointer-events-auto py-2.5 bg-[#F6F2EB]/95 backdrop-blur-md border-b border-[#DED7CC] shadow-[0_4px_20px_rgba(55,51,47,0.05)]'
            : 'opacity-0 -translate-y-full pointer-events-none py-2 bg-transparent'
        }`}
      >
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 flex items-center justify-between">
          {/* Brand Logo */}
          <button
            type="button"
            onClick={() => scrollToAnchor('hero')}
            className="flex items-center focus:outline-none cursor-pointer transition-opacity hover:opacity-80 shrink-0"
            aria-label="Kalas Distillery - Gå til toppen"
          >
            <img
              src={LOGO_SRC}
              alt="KALAS Logo"
              className="h-[42px] sm:h-[52px] w-auto object-contain transition-all duration-300"
            />
          </button>

          {/* Desktop Navigation Links */}
          <nav
            aria-label="Hovedmenu"
            className="hidden lg:flex items-center gap-6 xl:gap-8"
          >
            {NAV_ITEMS.map((item) => {
              const isActive = activeSection === item.id;
              return (
                <button
                  key={item.id}
                  type="button"
                  onClick={() => scrollToAnchor(item.id)}
                  className={`group relative py-2 font-['Montserrat'] text-xs uppercase tracking-[0.22em] transition-colors duration-300 cursor-pointer focus:outline-none ${
                    isActive
                      ? 'text-[#C99850] font-medium'
                      : 'text-[#37332F]/75 font-normal hover:text-[#37332F]'
                  }`}
                >
                  <span className="transition-colors">{item.label}</span>
                  {isActive && (
                    <span className="absolute bottom-0 left-0 right-0 h-[1.5px] bg-[#C99850]" />
                  )}
                </button>
              );
            })}
          </nav>

          {/* Right Action: Kontakt CTA + Mobile Menu Toggle */}
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => scrollToAnchor('contact')}
              className={`hidden lg:inline-flex items-center px-5 py-2.5 border text-xs uppercase tracking-[0.22em] transition-all duration-300 cursor-pointer group focus:outline-none ${
                activeSection === 'contact'
                  ? 'bg-[#37332F] text-[#F6F2EB] border-[#37332F]'
                  : 'bg-transparent border-[#37332F]/40 hover:border-[#37332F] hover:bg-[#37332F] hover:text-[#F6F2EB] text-[#37332F]'
              }`}
            >
              <span>Kontakt</span>
            </button>

            {/* Mobile Menu Hamburger Toggle */}
            <button
              type="button"
              onClick={() => setMobileMenuOpen((prev) => !prev)}
              className="lg:hidden w-10 h-10 border border-[#DED7CC] hover:border-[#37332F]/60 text-[#37332F] bg-[#F6F2EB] flex items-center justify-center transition-all duration-300 focus:outline-none cursor-pointer"
              aria-label={mobileMenuOpen ? 'Luk menu' : 'Åbn navigation'}
              aria-expanded={mobileMenuOpen}
            >
              {mobileMenuOpen ? (
                <X className="w-5 h-5 text-[#C99850]" aria-hidden="true" />
              ) : (
                <Menu className="w-5 h-5 text-[#37332F]" aria-hidden="true" />
              )}
            </button>
          </div>
        </div>
      </header>

      {/* Mobile Navigation Drawer */}
      <div
        className={`fixed inset-0 z-50 lg:hidden transition-all duration-500 ease-in-out ${
          mobileMenuOpen
            ? 'opacity-100 pointer-events-auto'
            : 'opacity-0 pointer-events-none'
        }`}
        aria-hidden={!mobileMenuOpen}
      >
        <div
          onClick={() => setMobileMenuOpen(false)}
          className="absolute inset-0 bg-[#37332F]/40 backdrop-blur-sm transition-opacity"
        />

        <div
          className={`relative h-full flex flex-col justify-between p-6 sm:p-8 bg-[#F6F2EB] border-b border-[#DED7CC] shadow-xl transition-transform duration-500 ease-out ${
            mobileMenuOpen ? 'translate-y-0' : '-translate-y-full'
          }`}
        >
          <div className="flex items-center justify-between pb-6 border-b border-[#DED7CC]">
            <img
              src={LOGO_SRC}
              alt="KALAS Logo"
              className="h-9 w-auto object-contain"
            />

            <button
              type="button"
              onClick={() => setMobileMenuOpen(false)}
              className="w-10 h-10 border border-[#DED7CC] text-[#37332F] flex items-center justify-center cursor-pointer hover:border-[#37332F]"
              aria-label="Luk menu"
            >
              <X className="w-5 h-5" aria-hidden="true" />
            </button>
          </div>

          <nav className="flex-1 py-8 flex flex-col justify-center space-y-3">
            {NAV_ITEMS.map((item) => {
              const isActive = activeSection === item.id;
              return (
                <button
                  key={item.id}
                  type="button"
                  onClick={() => scrollToAnchor(item.id)}
                  className={`group flex items-center justify-between w-full px-5 py-3.5 border transition-all duration-300 text-left cursor-pointer ${
                    isActive
                      ? 'bg-[#EAE5DD] border-[#DED7CC] text-[#C99850]'
                      : 'hover:bg-[#EAE5DD]/60 text-[#37332F]/80 hover:text-[#37332F] border-transparent'
                  }`}
                >
                  <span className="font-['Cormorant_Garamond'] text-2xl font-normal uppercase tracking-[0.15em]">
                    {item.label}
                  </span>
                  <ChevronRight
                    className={`w-4 h-4 transition-transform duration-300 ${
                      isActive ? 'text-[#C99850] translate-x-1' : 'text-[#37332F]/30 group-hover:translate-x-1'
                    }`}
                    aria-hidden="true"
                  />
                </button>
              );
            })}

            <button
              type="button"
              onClick={() => scrollToAnchor('contact')}
              className="mt-6 w-full flex items-center justify-center gap-3 px-6 py-4 bg-[#37332F] text-[#F6F2EB] font-['Montserrat'] text-xs uppercase tracking-[0.22em] font-medium hover:bg-[#2B2724] transition-colors cursor-pointer"
            >
              <span>Kontakt Destilleriet</span>
              <ArrowRight className="w-3.5 h-3.5" aria-hidden="true" />
            </button>
          </nav>

          <div className="pt-6 border-t border-[#DED7CC] flex items-center justify-between text-xs text-[#5E5852] font-normal tracking-wider">
            <span>Sydals · Holtvej 8</span>
            <span className="text-[#C99850]">Håndlavet spiritus</span>
          </div>
        </div>
      </div>
    </>
  );
}
